/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExpandableCard } from 'src/core/components/ExpandableCard';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { transformLabelToAnnotation } from 'src/core/utils/labelingUtils';
import { AssessmentCard } from 'src/languageStudio/appShell/prebuiltDemos/sentimentDemo/AssessmentCard';
import { AssessmentLabelTooltip } from 'src/languageStudio/appShell/prebuiltDemos/sentimentDemo/AssessmentLabelTooltip';
import { SentimentCard } from 'src/languageStudio/appShell/prebuiltDemos/sentimentDemo/SentimentCard';
import { ModifiedAssessment, ModifiedTarget } from 'src/languageStudio/appShell/prebuiltDemos/sentimentDemo/VisualizedSentimentResult';
import { expandableCardStyles } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { getLabel, getRelationAnnotations } from 'src/languageStudio/appShell/prebuiltDemos/utils/sentimentDemoUtils';
import { SentenceSwagger } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { Label } from 'src/modules/labeling/types/Label';
import { Labeler } from 'src/packages/react-text-annotator/lib/esm';

type AssessmentToken = { id: string; tokens: { startTokenIndex: number; endTokenIndex: number } };

export type SentenceCardProps = {
    sentenceIndex: number;
    isLabelerRTL: boolean;
    targets: ModifiedTarget[];
    sentence: SentenceSwagger;
    isLabelerWordTokenized: boolean;
    assessments: ModifiedAssessment[];
    targetsTokens: AssessmentToken[];
    assessmentsTokens: AssessmentToken[];
};

const messages = defineMessages({
    sentence: {
        id: 'languageStudio.sentenceCard.sentence',
        defaultMessage: 'Sentence {sentenceNumber}'
    },
    target: {
        id: 'languageStudio.sentenceCard.target',
        defaultMessage: 'target'
    },
    assessment: {
        id: 'languageStudio.sentenceCard.assessment',
        defaultMessage: 'assessment'
    },
    cardName: {
        id: 'languageStudio.sentenceCard.cardName',
        defaultMessage: 'Sentence card.'
    }
});

const theme = getScopedTheme('SentimentDemoPage');

export const SentenceCard = (props: SentenceCardProps) => {
    const { sentence, sentenceIndex, targets, targetsTokens, assessments, assessmentsTokens, isLabelerRTL, isLabelerWordTokenized } = props;
    const { localize } = useLocalization();
    const [isCardExpanded, setIsCardExpanded] = React.useState(sentenceIndex === 0);

    const annotations = React.useMemo(() => {
        const sentenceTokens = { startTokenIndex: sentence.offset, endTokenIndex: sentence.offset + sentence.length - 1 };

        const labels: Label[] = [
            ...targets
                .filter(a => a.sentenceIndex === sentenceIndex)
                .map(t =>
                    getLabel(
                        localize(messages.target),
                        t.targetIndex,
                        targetsTokens,
                        t.sentenceIndex,
                        'target',
                        sentenceTokens.startTokenIndex
                    )
                ),
            ...assessments
                .filter(o => o.sentenceIndex === sentenceIndex)
                .map(a =>
                    getLabel(
                        localize(messages.assessment),
                        a.assessmentIndex,
                        assessmentsTokens,
                        a.sentenceIndex,
                        'assessment',
                        sentenceTokens.startTokenIndex
                    )
                )
        ];

        return labels
            .map(label =>
                transformLabelToAnnotation(
                    label,
                    <AssessmentLabelTooltip label={label} assessments={assessments} targets={targets} />,
                    () => theme.labelColor
                )
            )
            .concat(
                getRelationAnnotations(
                    localize(messages.assessment),
                    theme.labelColor,
                    targets.filter(a => a.sentenceIndex === sentenceIndex),
                    assessments.filter(o => o.sentenceIndex === sentenceIndex),
                    targetsTokens,
                    assessmentsTokens,
                    sentenceTokens.startTokenIndex
                )
            );
    }, [sentence, targets, assessments]);

    return (
        <ExpandableCard
            id={`${sentenceIndex}`}
            isSeparatorDisplayed={false}
            isExpanded={sentenceIndex === 0}
            onExpansionChange={setIsCardExpanded}
            cardName={localize(messages.sentence, { sentenceNumber: sentenceIndex + 1 })}
            styles={expandableCardStyles(!isCardExpanded)}
            title={localize(messages.sentence, { sentenceNumber: sentenceIndex + 1 })}>
            <Stack tokens={{ childrenGap: 30 }}>
                <Labeler
                    text={sentence.text}
                    labelerHeight="unset"
                    labelerOverflow="unset"
                    annotations={annotations}
                    labelerConfigs={{
                        isDisabled: true,
                        isRtl: isLabelerRTL,
                        tokenizationType: isLabelerWordTokenized ? 'word' : 'character'
                    }}
                />

                <Stack horizontal tokens={{ childrenGap: 24 }}>
                    <SentimentCard isDocument={false} sentimentType={sentence.sentiment} confidenceDetails={sentence.confidenceScores} />

                    {sentence.targets?.map(t => (
                        <AssessmentCard
                            isTargetCard
                            targets={[t]}
                            key={`${t.offset}${t.length}`}
                            assessments={sentence.assessments.filter((_, assessmentIndex) =>
                                t.relations.some(
                                    relation => relation.ref === `#/documents/0/sentences/${sentenceIndex}/assessments/${assessmentIndex}`
                                )
                            )}
                        />
                    ))}
                </Stack>
            </Stack>
        </ExpandableCard>
    );
};
